import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import { testimonialsAPI, clientComplaintsAPI } from '../lib/api';
import stockImage from '../assets/images/stock.png';
import downloadImage from '../assets/images/download.jpg';
import TestimonialCarousel from '../components/TestimonialCarousel';
import SampleReportForm from '../components/SampleReportForm';
import { RewardSpinner, SpinnerButton } from '../components/RewardSpinner';

// Check if spinner is enabled via environment variable
const SPINNER_ENABLED = process.env.REACT_APP_REWARD_SPINNER_ENABLED === 'true';

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [complaintsData, setComplaintsData] = useState(null);
  const [complaintsLoading, setComplaintsLoading] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await testimonialsAPI.getTestimonials();
        const data = response.data?.data || response.data || [];
        // Take first 5 active testimonials and map to carousel format
        const formattedTestimonials = data.slice(0, 5).map(t => ({
          id: t.id,
          name: t.user_name,
          text: t.content,
          title: t.title,
          rating: t.rating,
          date: new Date(t.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        }));
        setTestimonials(formattedTestimonials);
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    const fetchComplaints = async () => {
      try {
        const response = await clientComplaintsAPI.getComplaints();
        const data = response.data?.data || response.data || [];
        
        // Transform API response to table format
        const rows = data.map(item => ({
          source: item.received_from,
          pending: item.pending_at_end_of_last_month,
          received: item.received,
          resolved: item.resolved,
          totalPending: item.total_pending,
          pendingOver3Months: item.pending_more_than_3_months,
          avgResolution: item.avg_resolution_time
        }));

        // Calculate grand totals
        const calculateTotal = (field) => {
          const values = data.map(item => item[field]);
          const hasNumber = values.some(v => !isNaN(parseInt(v)));
          if (!hasNumber) return 'Nil';
          return values.reduce((sum, v) => {
            const num = parseInt(v);
            return sum + (isNaN(num) ? 0 : num);
          }, 0).toString();
        };

        const grandTotal = {
          source: 'Grand Total',
          pending: calculateTotal('pending_at_end_of_last_month'),
          received: calculateTotal('received'),
          resolved: calculateTotal('resolved'),
          totalPending: calculateTotal('total_pending'),
          pendingOver3Months: calculateTotal('pending_more_than_3_months'),
          avgResolution: 'N.A.'
        };

        setComplaintsData({
          headers: ['Received from', 'Pending at the end of last month', 'Received', 'Resolved', 'Total Pending #', 'Pending complaints > 3 months', 'Average Resolution time (in days)'],
          rows,
          grandTotal
        });
      } catch (error) {
        console.error('Failed to fetch complaints:', error);
        // Fallback to empty state
        setComplaintsData({
          headers: ['Received from', 'Pending at the end of last month', 'Received', 'Resolved', 'Total Pending #', 'Pending complaints > 3 months', 'Average Resolution time (in days)'],
          rows: [],
          grandTotal: {
            source: 'Grand Total',
            pending: 'Nil',
            received: 'Nil',
            resolved: 'N.A',
            totalPending: 'Nil',
            pendingOver3Months: 'Nil',
            avgResolution: 'N.A.'
          }
        });
      } finally {
        setComplaintsLoading(false);
      }
    };

    fetchTestimonials();
    fetchComplaints();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#1b2d4a] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://climbcapital.in/assets/images/hero-el.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6 leading-tight">
                Let our <span className="text-yellow-400">expert analysts</span> do the research for you
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Unlock your financial freedom with CLIMB CAPITAL
              </p>
              <div className="flex items-center gap-4">
                <Link
                  to="/pricing"
                  className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-yellow-500 transition-all flex items-center gap-2 hover:scale-105"
                >
                  SEE OUR PLANS
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/about-us"
                  className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors"
                >
                  <div className="bg-yellow-400 text-gray-900 p-3 rounded-full">
                    <Play size={20} fill="currentColor" />
                  </div>
                  <span className="font-semibold">How We Work</span>
                </Link>
              </div>
            </div>
            <div>
              <img
                src={stockImage}
                alt="Financial Analyst"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Complaints Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            Number Of Client's Complaints
          </h2>
          {complaintsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
            </div>
          ) : complaintsData ? (
            <div className="bg-white rounded-lg shadow-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1b2d4a] text-white">
                  <tr>
                    {complaintsData.headers.map((header, index) => (
                      <th key={index} className="px-6 py-4 text-left text-sm font-semibold">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {complaintsData.rows.map((row, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{row.source}</td>
                      <td className="px-6 py-4 text-sm">{row.pending}</td>
                      <td className="px-6 py-4 text-sm">{row.received}</td>
                      <td className="px-6 py-4 text-sm">{row.resolved}</td>
                      <td className="px-6 py-4 text-sm">{row.totalPending}</td>
                      <td className="px-6 py-4 text-sm">{row.pendingOver3Months}</td>
                      <td className="px-6 py-4 text-sm">{row.avgResolution}</td>
                    </tr>
                  ))}
                  <tr className="bg-yellow-50 font-semibold">
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.source}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.pending}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.received}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.resolved}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.totalPending}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.pendingOver3Months}</td>
                    <td className="px-6 py-4 text-sm">{complaintsData.grandTotal.avgResolution}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No complaints data available
            </div>
          )}
          <div className="text-center mt-6">
            <Link
              to="/detailed-complaints"
              className="text-blue-600 hover:text-blue-800 font-semibold hover:underline"
            >
              Monthly / Annual Complaints
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-4 text-center">
            ‸Average Resolution time is the sum total of time taken to resolve each complaint in days, 
            in the current month divided by total number of complaints resolved in the current month.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            What Our Clients Say
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Read testimonials from our satisfied clients
          </p>
          {testimonialsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
            </div>
          ) : (
            <TestimonialCarousel testimonials={testimonials} />
          )}
        </div>
      </section>

      {/* Sample Report Section */}
      <section id="download-report" className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={downloadImage}
                alt="Sample Report"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Get a Sample stock idea research report
              </h2>
              <SampleReportForm />
            </div>
          </div>
        </div>
      </section>

      {/* Reward Spinner - Only shown when enabled via env var */}
      {SPINNER_ENABLED && (
        <>
          <SpinnerButton onClick={() => setShowSpinner(true)} />
          <RewardSpinner 
            isOpen={showSpinner} 
            onClose={() => setShowSpinner(false)} 
          />
        </>
      )}
    </div>
  );
};

export default Home;
